import { useCallback } from "react";
import { useStripe } from "@stripe/react-stripe-js";
import type {
  StripeError,
  StripePaymentElementChangeEvent,
} from "@stripe/stripe-js";
import { useFormContext } from "react-hook-form";
import { addBugsnagBreadcrumb } from "@/react/helpers/bugsnagHelpers";
import {
  EMAIL_REGEX,
  PASSWORD_REGEX,
} from "@circle-react/components/OnboardingPage/helpers/constants";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { cannotPurchaseAgain } from "@circle-react/helpers/paywallCheckoutHelpers";
import { CheckoutError } from "../CheckoutError";
import { reportCheckoutError } from "../checkoutErrorHelpers";
import { buildPayButtonLabel } from "../helpers";
import { useOnSubmitWithPMC } from "./useOnSubmitWithPMC";

export const usePayment = () => {
  const { watch } = useFormContext();
  const {
    currentUser,
    hasAlreadyPurchased,
    loginUrl,
    communityRootUrl,
    paywall,
    getSelectedPrice,
    previewMutation,
    paymentError,
    isCardInfoValid,
    isPaywallActive,
    isCardInfoComplete,
    setIsCardInfoComplete,
    isTaxIdMandatory,
    shouldShowTaxIdForm,
    setStripePaymentMethodType,
    setIsMemberUsingSavedPaymentMethod,
    currentCommunity,
    currentCommunityMember,
  } = usePaywallCheckoutContext();
  const stripe = useStripe();
  const selectedPrice = getSelectedPrice();

  const {
    name,
    email,
    password,
    termsOfService,
    community_member_billing_info_address_attributes_complete:
      isMemberAddressComplete,
    community_member_billing_info_attributes: { tax_id_value, tax_id_type },
  } = watch();

  const isValidEmail = EMAIL_REGEX.test(email);
  const shouldDisableSubmitOnInvalidEmail = !isValidEmail;

  const isValidPassword = PASSWORD_REGEX.test(password);
  const shouldDisableSubmitOnInvalidPassword = !isValidPassword;

  const isSubmitDisabled =
    !isMemberAddressComplete ||
    !termsOfService ||
    (!currentUser &&
      (!name ||
        shouldDisableSubmitOnInvalidEmail ||
        shouldDisableSubmitOnInvalidPassword)) ||
    !isCardInfoValid ||
    !isPaywallActive ||
    paymentError.disablePayButton ||
    cannotPurchaseAgain(hasAlreadyPurchased, paywall) ||
    previewMutation.isLoading ||
    previewMutation.isError ||
    (isTaxIdMandatory &&
      shouldShowTaxIdForm &&
      (!tax_id_value || !tax_id_type));

  const { data: preview } = previewMutation;
  const payButtonLabel = buildPayButtonLabel({
    isPaywallActive,
    selectedPrice,
    preview,
    currency: paywall.currency,
  });

  const { onSubmit } = useOnSubmitWithPMC();

  // let's check if credit card details are complete
  const handleOnChangeCard = useCallback(
    (e: StripePaymentElementChangeEvent) => {
      setStripePaymentMethodType(e.value.type);

      if (e.complete) {
        addBugsnagBreadcrumb("usePayment.onChange - completed payment method", {
          metadata: {
            payment_method_id: e.value?.payment_method?.id,
            payment_method_type: e.value?.type,
          },
          type: "manual",
        });
        setIsCardInfoComplete(true);
      } else if (isCardInfoComplete) {
        setIsCardInfoComplete(false);
      }

      // Stripe's Payment Element assigns a `payment_method` object to the event if the member is using
      // an existing payment method for the purchase. Either when the Element loads with the member's
      // default payment method or when the member selects one of their saved payment methods.
      setIsMemberUsingSavedPaymentMethod(!!e.value?.payment_method?.id);
    },
    [
      isCardInfoComplete,
      setIsCardInfoComplete,
      setIsMemberUsingSavedPaymentMethod,
      setStripePaymentMethodType,
    ],
  );

  const handleOnReadyCard = (e: any) => currentUser && e.focus();

  const onLoadError = useCallback(
    (event: { elementType: string; error: StripeError }) => {
      const checkoutError = CheckoutError.fromStripeError(
        "usePayment.onLoadError",
        event.error,
      );
      reportCheckoutError(checkoutError, {
        caller: checkoutError.caller,
        communityId: currentCommunity?.id,
        communityMemberId: currentCommunityMember?.id,
        communityMemberUserId: currentUser?.id,
        extra: checkoutError.getExtra(),
        paywallId: paywall?.id,
      });
    },
    [currentCommunity, currentCommunityMember, currentUser, paywall],
  );

  return {
    paymentError,
    isSubmitDisabled,
    selectedPrice,
    currentUser,
    stripe,
    loginUrl,
    communityRootUrl,
    payButtonLabel,
    hasAlreadyPurchased,
    handleOnChangeCard,
    handleOnReadyCard,
    onLoadError,
    onSubmit,
  };
};
